import React, { useState, useMemo } from 'react';
import { 
    format, 
    startOfMonth, 
    endOfMonth, 
    eachDayOfInterval, 
    isSameDay, 
    addMonths, 
    subMonths, 
    startOfWeek, 
    endOfWeek,
    isToday,
    isValid
} from 'date-fns';
import Button from '../../../ui/Button';
import Icon from '../../../ui/Icon';
import { ICONS } from '../../../ui/icon-map';
import Badge from '../../../ui/Badge';
import './ReturnCarsCalendar.css';

/**
 * ReturnCarsCalendar
 * Display daily return collections in a month calendar view.
 * 
 * @param {Array} data - List of contracts with dropoffDate
 * @param {Function} onDayClick - Callback when a day is clicked to show specific contracts
 */
const ReturnCarsCalendar = ({ data, onDayClick }) => {
    const [currentMonth, setCurrentMonth] = useState(new Date());

    // Helper to parse localized/format-scattered dates
    const parseFlexibleDate = (dateStr) => {
        if (!dateStr) return null;
        // Try DD/MM/YYYY
        const parts = dateStr.split(/[-/]/);
        if (parts.length === 3) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const year = parts[2].length === 2 ? 2000 + parseInt(parts[2], 10) : parseInt(parts[2], 10);
            const d = new Date(year, month, day);
            return isValid(d) ? d : null;
        }
        const d = new Date(dateStr);
        return isValid(d) ? d : null;
    };

    // Calculate daily counts for the current month view
    const calendarData = useMemo(() => {
        const monthStart = startOfMonth(currentMonth);
        const monthEnd = endOfMonth(currentMonth);
        const calendarStart = startOfWeek(monthStart);
        const calendarEnd = endOfWeek(monthEnd);

        const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

        return days.map(day => {
            const dayContracts = data.filter(item => {
                const dropoff = parseFlexibleDate(item.dropoffDate);
                return dropoff && isSameDay(dropoff, day);
            });

            return {
                day,
                count: dayContracts.length,
                contracts: dayContracts,
                isCurrentMonth: day.getMonth() === currentMonth.getMonth()
            };
        });
    }, [currentMonth, data]);

    const handlePrevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
    const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

    return (
        <div className="return-cars-calendar-container">
            <header className="flex flex-wrap items-center justify-between gap-3 mb-4 bg-white p-3 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" iconOnly aria-label="Previous month" onClick={handlePrevMonth} leadingIcon={<Icon glyph={ICONS.chevronLeft} size="sm" />} />
                    <h2 className="text-base font-semibold text-gray-900 min-w-[140px] text-center" aria-live="polite">
                        {format(currentMonth, 'MMMM yyyy')}
                    </h2>
                    <Button variant="ghost" size="sm" iconOnly aria-label="Next month" onClick={handleNextMonth} leadingIcon={<Icon glyph={ICONS.chevronRight} size="sm" />} />
                </div>

                <Badge variant="danger">
                    Total Returns: {data.length}
                </Badge>
            </header>

            <div className="calendar-grid bg-white rounded-lg overflow-hidden border border-gray-200">
                {/* Week Days Header */}
                <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="py-2 text-center font-semibold text-gray-500 text-[11px] uppercase tracking-wide">
                            {day}
                        </div>
                    ))}
                </div>

                {/* Days Grid */}
                <div className="grid grid-cols-7 auto-rows-[96px]">
                    {calendarData.map(({ day, count, contracts, isCurrentMonth }, idx) => {
                        const today = isToday(day);
                        const cellClass = `calendar-day p-2 border-r border-b border-gray-200 text-left
                                ${!isCurrentMonth ? 'bg-gray-50' : 'bg-white'}
                                ${today ? 'is-today' : ''}
                                ${count > 0 ? 'is-clickable' : ''}`;
                        const inner = (
                            <>
                                {/* Day Number - Top Left */}
                                <span className={`absolute top-1.5 left-2 text-xs tabular-nums ${today ? 'font-semibold text-purple-700' : isCurrentMonth ? 'text-gray-600' : 'text-gray-500'}`}>
                                    {format(day, 'dd')}
                                </span>

                                {/* Return Count */}
                                <span className="flex flex-col items-center justify-center flex-1">
                                    {count > 0 && (
                                        <>
                                            <span className="text-2xl font-semibold text-gray-900 tabular-nums">{count}</span>
                                            <span className="text-xs text-gray-500">Returns</span>
                                        </>
                                    )}
                                </span>
                            </>
                        );
                        return count > 0 ? (
                            <button
                                key={idx}
                                type="button"
                                onClick={() => onDayClick(day, contracts)}
                                className={cellClass}
                                aria-label={`${format(day, 'd MMMM yyyy')}: ${count} returns`}
                            >
                                {inner}
                            </button>
                        ) : (
                            <div key={idx} className={cellClass}>
                                {inner}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default ReturnCarsCalendar;
